import { Observer, UsersResponse, convertObserver } from '@nextforce/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useServer } from 'app/server/context'
import { produce } from 'immer'
import { compareUsersByName } from 'management_team/util'
import { convertUserRoleToQueryParam } from './shared/convert'
import keys from './shared/keys'
import { AddEditUserQueryParams } from './shared/types'

export function useAddUser() {
  const { server } = useServer()
  const client = useQueryClient()
  return useMutation({
    mutationFn: addUser,
    onSuccess: async (user, { role, companyId }) => {
      const queryKey = keys.list(companyId, role)
      const userListData = client.getQueryData<Observer[]>(queryKey)
      if (userListData) {
        client.setQueryData<Observer[]>(
          queryKey,
          produce(userListData, updatedData => {
            const index = userListData.findIndex(d => d.id === user.id)
            if (index === -1) {
              updatedData.push(user)
            } else {
              updatedData[index] = user
            }
            updatedData.sort(compareUsersByName)
          })
        )
        client.setQueryData<Observer>(keys.detail(companyId, user.id), user)
      }
    },
  })

  async function addUser(props: AddEditUserQueryParams): Promise<Observer> {
    const response = await server.post<UsersResponse.Observer>(
      getUrl(props),
      getBody(props)
    )

    return convertObserver(response)
  }
}

function getUrl({
  storeIds,
  companyId,
  inviteToNext,
  role,
}: AddEditUserQueryParams) {
  if (!companyId) {
    throw new Error('Company id invalid')
  }
  if (role === 'observer') {
    return `/v2/users/observers/${companyId}?store_ids=${
      storeIds?.length ? storeIds : ''
    }&send_welcome_notification=${inviteToNext}`
  } else if (role === 'staff' || role === 'guest') {
    return `/v2/users/${companyId}`
  }
  throw new Error('Role invalid')
}

function getBody({
  firstName,
  lastName,
  phoneNumber,
  role,
  jobTitle,
}: AddEditUserQueryParams) {
  return {
    first_name: firstName,
    last_name: lastName,
    phone_number: phoneNumber,
    role: convertUserRoleToQueryParam(role),
    job_title: jobTitle,
  }
}
